import { useMemo } from "react";

import { Controller, type UseFormReturn } from "react-hook-form";
import { type z } from "zod";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { CurrencyInput, NumberInput } from "@components/v2";
import { FormattedNumberInput } from "@components/v2/inputs/formatted-number-input";
import { TogglablePills } from "@features/mmm/components/shared/generic/toggleable-pills";
import { useFeatureFlags } from "@hooks/http";

import { KpiType, type Kpi } from "../types";

import {
  FormKpiType,
  type ClientSettingSchema,
  type FormKpi,
} from "./client-setting-form";

type Props = {
  formMeta: UseFormReturn<z.infer<typeof ClientSettingSchema>>;
  availableKpis: (Kpi & { value: string; displayValue: string })[];
  setSelectedKpi: (kpi: FormKpi) => void;
  astTokens: string[];
  selectedKpi: FormKpi;
};

export const SettingKpiField = ({
  formMeta,
  availableKpis,
  setSelectedKpi,
  astTokens,
  selectedKpi,
}: Props) => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const kpiMap = useMemo<Record<string, Props["availableKpis"][number]>>(
    () =>
      availableKpis.reduce(
        (acc, kpi) => ({ ...acc, [String(kpi.id)]: kpi }),
        {},
      ),
    [availableKpis],
  );

  return (
    <div className="border-b border-v2-secondary px-6 pt-5 pb-2">
      <label className="block font-semibold">2. Select a KPI Objective</label>
      <p className="text-sm mb-2">
        Your KPI object is the primary metric that budget mixes are optimized
        for.
      </p>
      <div className="flex justify-between mb-4">
        <Controller
          control={formMeta.control}
          name="kpi"
          render={({ field: { value, onChange } }) => {
            return (
              <div className="flex flex-col">
                <TogglablePills
                  pillWidthClassname="w-auto"
                  pills={availableKpis}
                  onClick={(val: string) => {
                    const clickedKpi = kpiMap[val];
                    const kpiType =
                      clickedKpi && clickedKpi.data_columns.length > 1
                        ? FormKpiType.COMPLEX
                        : FormKpiType.SIMPLE;
                    const kpiEquation = clickedKpi?.equation;
                    setSelectedKpi({
                      type: kpiType,
                      id: val,
                      name: clickedKpi?.friendly_name ?? "Unknown KPI",
                      equation: kpiEquation,
                    });
                    onChange(val);
                    formMeta.setValue("max_cost_per_unit_kpi", "");
                    formMeta.setValue("profit_value_per_unit_kpi", "");
                  }}
                  selectedValue={value}
                  showCount={2}
                />
                {user.isAdmin && (
                  <>
                    <div className="text-md font-semibold mt-3">
                      <GenericTooltip content="Only available for NB employees">
                        Equation
                      </GenericTooltip>
                    </div>
                    <div className="flex  max-w-2xl  items-center flex-wrap">
                      {astTokens.map((token, i) => (
                        <div
                          key={i}
                          className="mt-2 mr-2 flex items-center rounded bg-v2-secondary px-3.5 py-0.5 text-sm"
                        >
                          {token}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          }}
        />
        {hasFeatureFlag("allowDesiredKpiValueForMMM") && (
          <Controller
            name="desired_kpi_value"
            control={formMeta.control}
            render={({ field: { value, onChange } }) => {
              if (kpiMap[selectedKpi?.id ?? ""]?.kpi_type === KpiType.REVENUE) {
                return (
                  <CurrencyInput
                    onChange={onChange}
                    value={value}
                    placeholder="KPI Target"
                    required={false}
                    className="font-sans text-md"
                  />
                );
              }
              return (
                <FormattedNumberInput
                  onChange={onChange}
                  value={value}
                  placeholder="KPI Target"
                  required={false}
                  decimalPlaces={0}
                  className="font-sans text-md"
                />
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
